import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Plus, Search, Filter, MoreVertical, Edit2, Trash2, Eye, EyeOff } from 'lucide-react';
import { ListingCard } from '../components/ListingCard';
import { SegmentedControl } from '../components/SegmentedControl';
import { Button } from '../components/Button';
import { Modal } from '../components/Modal';
interface InventoryProps {
  onBack: () => void;
  onCreateListing: () => void;
}
export function Inventory({
  onBack,
  onCreateListing
}: InventoryProps) {
  const [selectedTab, setSelectedTab] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedListingId, setSelectedListingId] = useState<string | null>(null);
  // Mock data
  const [listings, setListings] = useState([{
    id: '1',
    title: 'Professional Power Drill Set',
    image: 'https://images.unsplash.com/photo-1504148455328-c376907d081c?w=800&q=80',
    price: 15,
    priceUnit: 'per day',
    distance: 'Your listing',
    rating: 4.8,
    reviewCount: 24,
    category: 'Tools',
    status: 'active'
  }, {
    id: '2',
    title: 'Camping Tent (4-Person)',
    image: 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=800&q=80',
    price: 25,
    priceUnit: 'per day',
    distance: 'Your listing',
    rating: 4.9,
    reviewCount: 12,
    category: 'Outdoors',
    status: 'active'
  }, {
    id: '3',
    title: 'Pressure Washer 3000 PSI',
    image: 'https://images.unsplash.com/photo-1520342868574-5fa3804e551c?w=800&q=80',
    price: 40,
    priceUnit: 'per day',
    distance: 'Your listing',
    rating: 0,
    reviewCount: 0,
    category: 'Tools',
    status: 'inactive'
  }]);
  const filteredListings = listings.filter(listing => {
    const matchesSearch = listing.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = selectedTab === 'All' || selectedTab === 'Active' && listing.status === 'active' || selectedTab === 'Inactive' && listing.status === 'inactive';
    return matchesSearch && matchesTab;
  });
  const handleDelete = () => {
    if (selectedListingId) {
      setListings(listings.filter(l => l.id !== selectedListingId));
      setShowDeleteModal(false);
      setSelectedListingId(null);
    }
  };
  const toggleStatus = (id: string) => {
    setListings(listings.map(l => {
      if (l.id === id) {
        return {
          ...l,
          status: l.status === 'active' ? 'inactive' : 'active'
        };
      }
      return l;
    }));
  };
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const item = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0
    }
  };
  return <div className="min-h-screen bg-cream pb-24" data-id="element-1176">
      {/* Header */}
      <div className="bg-white border-b border-sand sticky top-0 z-10" data-id="element-1177">
        <div className="max-w-md mx-auto px-4 pt-4 pb-3" data-id="element-1178">
          <div className="flex items-center gap-3 mb-4" data-id="element-1179">
            <button onClick={onBack} className="p-2 -ml-2 rounded-full hover:bg-sand/20 transition-colors" data-id="element-1180">
              <ArrowLeft size={24} className="text-charcoal" data-id="element-1181" />
            </button>
            <h1 className="text-xl font-bold text-charcoal" data-id="element-1182">My Inventory</h1>
            <button onClick={onCreateListing} className="ml-auto p-2 rounded-full bg-dark-green/10 text-dark-green hover:bg-dark-green/20 transition-colors" data-id="element-1183">
              <Plus size={24} data-id="element-1184" />
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 mb-4" data-id="element-1185">
            <div className="bg-cream rounded-ios p-3 text-center border border-sand" data-id="element-1186">
              <div className="text-2xl font-bold text-charcoal" data-id="element-1187">
                {listings.length}
              </div>
              <div className="text-xs text-moss-gray" data-id="element-1188">Total</div>
            </div>
            <div className="bg-cream rounded-ios p-3 text-center border border-sand" data-id="element-1189">
              <div className="text-2xl font-bold text-dark-green" data-id="element-1190">
                {listings.filter(l => l.status === 'active').length}
              </div>
              <div className="text-xs text-moss-gray" data-id="element-1191">Active</div>
            </div>
            <div className="bg-cream rounded-ios p-3 text-center border border-sand" data-id="element-1192">
              <div className="text-2xl font-bold text-moss-gray" data-id="element-1193">
                {listings.filter(l => l.status === 'inactive').length}
              </div>
              <div className="text-xs text-moss-gray" data-id="element-1194">Inactive</div>
            </div>
          </div>

          <SegmentedControl options={['All', 'Active', 'Inactive']} selected={selectedTab} onChange={setSelectedTab} data-id="element-1195" />
        </div>
      </div>

      {/* Search */}
      <div className="max-w-md mx-auto px-4 py-4" data-id="element-1196">
        <div className="relative mb-4" data-id="element-1197">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-moss-gray" size={20} data-id="element-1198" />
          <input type="text" placeholder="Search your listings..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-white border border-sand rounded-ios text-charcoal placeholder-moss-gray focus:outline-none focus:ring-2 focus:ring-dark-green" data-id="element-1199" />
        </div>

        {/* Listings */}
        <motion.div variants={container} initial="hidden" animate="show" className="space-y-4" data-id="element-1200">
          {filteredListings.length === 0 ? <div className="text-center py-12" data-id="element-1201">
              <div className="w-16 h-16 bg-sand/30 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1202">
                <Filter size={32} className="text-moss-gray" data-id="element-1203" />
              </div>
              <h3 className="text-lg font-semibold text-charcoal mb-2" data-id="element-1204">
                No listings found
              </h3>
              <p className="text-moss-gray mb-6" data-id="element-1205">
                {searchQuery ? 'Try adjusting your search terms' : "You haven't added any listings yet"}
              </p>
              {!searchQuery && <Button variant="primary" onClick={onCreateListing} data-id="element-1206">
                  Create First Listing
                </Button>}
            </div> : filteredListings.map(listing => <motion.div key={listing.id} variants={item} className="bg-white rounded-ios-lg shadow-ios overflow-hidden" data-id="element-1207">
                <div className="relative" data-id="element-1208">
                  <img src={listing.image} alt={listing.title} className={`w-full h-48 object-cover ${listing.status === 'inactive' ? 'opacity-60 grayscale' : ''}`} data-id="element-1209" />
                  <div className="absolute top-3 right-3 flex gap-2" data-id="element-1210">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${listing.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-1211">
                      {listing.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                <div className="p-4" data-id="element-1212">
                  <div className="flex justify-between items-start mb-2" data-id="element-1213">
                    <div data-id="element-1214">
                      <h3 className="font-semibold text-charcoal text-lg" data-id="element-1215">
                        {listing.title}
                      </h3>
                      <p className="text-dark-green font-medium" data-id="element-1216">
                        ${listing.price}{' '}
                        <span className="text-moss-gray text-sm font-normal" data-id="element-1217">
                          / {listing.priceUnit}
                        </span>
                      </p>
                    </div>
                    <div className="flex items-center gap-1 text-sm text-moss-gray" data-id="element-1218">
                      <span className="text-yellow-500" data-id="element-1219">★</span>
                      <span data-id="element-1220">{listing.rating > 0 ? listing.rating : 'New'}</span>
                      {listing.reviewCount > 0 && <span data-id="element-1221">({listing.reviewCount})</span>}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mt-4 pt-4 border-t border-sand" data-id="element-1222">
                    <button onClick={() => toggleStatus(listing.id)} className="flex-1 py-2 px-3 rounded-ios border border-sand text-sm font-medium text-charcoal hover:bg-sand/10 transition-colors flex items-center justify-center gap-2" data-id="element-1223">
                      {listing.status === 'active' ? <EyeOff size={16} data-id="element-1224" /> : <Eye size={16} data-id="element-1225" />}
                      {listing.status === 'active' ? 'Deactivate' : 'Activate'}
                    </button>
                    <button className="p-2 rounded-ios border border-sand text-charcoal hover:bg-sand/10 transition-colors" data-id="element-1226">
                      <Edit2 size={16} data-id="element-1227" />
                    </button>
                    <button onClick={() => {
                setSelectedListingId(listing.id);
                setShowDeleteModal(true);
              }} className="p-2 rounded-ios border border-red-200 text-red-500 hover:bg-red-50 transition-colors" data-id="element-1228">
                      <Trash2 size={16} data-id="element-1229" />
                    </button>
                  </div>
                </div>
              </motion.div>)}
        </motion.div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Delete Listing" data-id="element-1230">
        <div className="space-y-4" data-id="element-1231">
          <p className="text-charcoal" data-id="element-1232">
            Are you sure you want to delete this listing? This action cannot be
            undone.
          </p>
          <div className="flex gap-3" data-id="element-1233">
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)} className="flex-1" data-id="element-1234">
              Cancel
            </Button>
            <Button variant="primary" onClick={handleDelete} className="flex-1 bg-red-500 hover:bg-red-600 border-red-500" data-id="element-1235">
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}